// React
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
// MUI
import { Grid, Box, Button } from "@mui/material";
import Iconify from "../../../components/iconify/Iconify";
// Constants
import EntityConstants from "../../../configs/EntityConstants";
import MessageContants from "../../../configs/MessageContants";
// Service
import { get, post, put } from "../../../services/FetchHelper";
import SpinService from "../../../services/SpinService";
// Utils
import {
    InitialAccountState,
    AccountSchema,
    AccountFormFields,
} from "./Configuration.utils";
// Styles
import { StyledDrawerForm } from "../../../configs/GoStyles";
//Third party
import { Form, Formik } from "formik";
import toast from "react-hot-toast";
import GoForm from "../../../components/Inputs/GoForm";
import PermissionConstants from "../../../configs/PermissionConstants";
import usePermissions from "../../../hooks/usePermissions";

const Account = ({ id, onClose }) => {
    const permissions = usePermissions();
    const spinService = SpinService();
    const [account, setAccount] = useState(InitialAccountState);
    const [formFields, setFormFields] = useState(AccountFormFields);

    const attachOptions = (name, options) => {
        setFormFields(prevFormFields =>
            prevFormFields.map(field =>
                field.name === name && (field.type === "select" || field.type === "multiselect")
                    ? { ...field, options: options }
                    : field
            )
        );
    };

    const getGroupHeaders = async () => {
        spinService.on();
        const response = await get(EntityConstants.GROUP_HEADERS, "max=all");
        const data = await response.json();
        attachOptions("group_header_id", data);
        spinService.off();
    };

    const getMainGroups = async (group_header_id) => {
        spinService.on();
        const response = await get(EntityConstants.MAIN_GROUPS, `max=all&group_header_id=${group_header_id}`);
        const data = await response.json();
        attachOptions("main_group_id", data);
        spinService.off();
    };

    const getSubAccounts = async (main_group_id) => {
        spinService.on();
        const response = await get(EntityConstants.SUB_ACCOUNTS, `max=all&main_group_id=${main_group_id}`);
        const data = await response.json();
        attachOptions("sub_account_id", data);
        spinService.off();
    };


    const getAccount = async (id) => {
        spinService.on();
        const response = await get(EntityConstants.ACCOUNTS + `/${id}`);
        const data = await response.json();
        setAccount(data);
        getMainGroups(data.group_header_id);
        getSubAccounts(data.main_group_id);
        spinService.off();
    };

    const localHandleChange = (e) => {
        if (e.target.name === "group_header_id") {
            getMainGroups(e.target.value);
        }
        if (e.target.name === "main_group_id") {
            getSubAccounts(e.target.value);
        }
    };

    const handleSubmit = async (values) => {
        spinService.on();
        const isNewAccount = values.id === undefined;
        const url = isNewAccount ? EntityConstants.ACCOUNTS : EntityConstants.ACCOUNTS + `/${values.id}`;
        const method = isNewAccount ? post : put;
        const response = await method(url, { account: values });
        if ([200, 201].indexOf(response.status) > -1) {
            toast.success(isNewAccount ? MessageContants.ACCOUNT_CREATED : MessageContants.ACCOUNT_UPDATED);
            onClose();
        }
        spinService.off();
    };

    useEffect(() => {
        getGroupHeaders();
        if (id !== undefined) {
            getAccount(Number(id));
        }
    }, [id]);

    return (
        <Formik
            enableReinitialize
            initialValues={account}
            validationSchema={AccountSchema}
            onSubmit={(values) => handleSubmit(values)}
        >
            {({
                values,
                touched,
                errors,
                dirty,
                isSubmitting,
                handleChange,
                handleBlur,
                handleSubmit,
            }) => (
                <Form noValidate onSubmit={handleSubmit}>
                    <StyledDrawerForm>
                        <Grid
                            container
                            maxWidth="sm"
                            rowSpacing={1}
                            columnSpacing={{ xs: 1, sm: 2, md: 2 }}
                        >
                            {formFields.map((formField, index) => {
                                return (
                                    <Grid key={index} item xs={12} sm={12} md={6} lg={6}>
                                        <GoForm
                                            config={formField}
                                            values={values}
                                            touched={touched}
                                            errors={errors}
                                            handleChange={(e) => { handleChange(e); localHandleChange(e) }}
                                        />
                                    </Grid>
                                );
                            })}
                        </Grid>
                        <Box sx={{ p: 2, textAlign: "right" }}>
                            <Button
                                variant="contained"
                                color="error"
                                startIcon={<Iconify icon="eva:close-circle-outline" />}
                                onClick={() => onClose()}
                            >
                                Close
                            </Button>
                            &nbsp;&nbsp;
                            {((values.id === undefined && permissions.has(PermissionConstants.ACCOUNT_CREATE)) || (values.id !== undefined && permissions.has(PermissionConstants.ACCOUNT_UPDATE))) &&
                                <Button
                                    variant="contained"
                                    startIcon={<Iconify icon="eva:save-fill" />}
                                    type="submit"
                                >
                                    {values.id === undefined ? 'Save' : 'Update'}
                                </Button>
                            }
                        </Box>
                    </StyledDrawerForm>
                </Form>
            )}
        </Formik>
    );
};

Account.propTypes = {
    id: PropTypes.number,
    onClose: PropTypes.any,
};

export default Account;
